import type {
  HomeFilterKey,
  HomeProfile,
  HomeScope,
} from "../types/home.types";

export const HOME_SCOPES: HomeScope[] = ["meus", "celula", "todos"];

export const HOME_SCOPE_LABELS: Record<HomeScope, string> = {
  meus: "Meus projetos",
  celula: "Minha célula",
  todos: "Todos",
};

export const HOME_PROFILE_LABELS: Record<HomeProfile, string> = {
  COORDENADOR: "Coordenador",
  LIDER: "Líder",
  ADMIN: "Administrador",
};

/** Rótulo do filtro na faixa da lista de Projetos. */
export const HOME_FILTER_LABELS: Record<HomeFilterKey, string> = {
  hoje: "Eventos hoje",
  amanha: "Eventos amanhã (D+1)",
  aprovacaoParada: "Em aprovação há +5 dias",
  semAprovacao: "Evento próximo sem orçamento aprovado",
  proximos7: "Início nos próximos 7 dias",
  realizadoParado: "Realizado sem Pré-fechamento há +5 dias",
  aguardandoCliente: "Aguardando Cliente há +10 dias",
  pendenteAuditoria: "Pendente Auditoria",
  perdidos: "Status: Perdido (últimos 30 dias)",
  cancelados: "Status: Cancelados (últimos 30 dias)",
  declinados: "Status: Declinado (últimos 30 dias)",
  ativos: "Projetos ativos",
  devolvidos: "Devolvidos",
};

export function isHomeFilterKey(value: unknown): value is HomeFilterKey {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(HOME_FILTER_LABELS, value)
  );
}

export function isHomeScope(value: unknown): value is HomeScope {
  return HOME_SCOPES.includes(value as HomeScope);
}

export function homeUrl(scope?: HomeScope | null): string {
  return scope ? `/inicio?escopo=${scope}` : "/inicio";
}

export function homeProjectsUrl(key: HomeFilterKey, scope: HomeScope): string {
  return `/projetos?filtro=${key}&escopo=${scope}`;
}

/**
 * Filtro da lista de Projetos aberta pelo Início: um card (`filtro`), um
 * status de fase (`fase`) e/ou uma linha da carga (`coordenador`, `celula`).
 * `label` é o texto da faixa "Filtro vindo do Início".
 */
export interface HomeListFilter {
  scope: HomeScope;
  homeFilter?: HomeFilterKey;
  homeStatus?: string;
  homeCoordinator?: string;
  homeCell?: string;
  label: string;
}

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const STATUS_KEY = /^[A-Z_]+$/;

/** URL da lista para um status de fase, coordenador ou célula do Início. */
export function homeListUrl(params: {
  scope: HomeScope;
  filter?: HomeFilterKey;
  status?: string;
  coordinator?: string;
  cell?: string;
  label?: string;
}): string {
  const query = new URLSearchParams();
  if (params.filter) query.set("filtro", params.filter);
  if (params.status) query.set("fase", params.status);
  if (params.coordinator) query.set("coordenador", params.coordinator);
  if (params.cell) query.set("celula", params.cell);
  query.set("escopo", params.scope);
  if (params.label) query.set("rotulo", params.label);
  return `/projetos?${query.toString()}`;
}

/** Lê o filtro do Início da query string da lista (ou null, sem filtro). */
export function parseHomeListFilter(search: string): HomeListFilter | null {
  const query = new URLSearchParams(search);
  const scope = query.get("escopo");
  if (!isHomeScope(scope)) return null;

  const filter = query.get("filtro");
  const status = query.get("fase");
  const coordinator = query.get("coordenador");
  const cell = query.get("celula");
  const label = query.get("rotulo")?.trim() ?? "";

  const homeFilter = isHomeFilterKey(filter) ? filter : undefined;
  const homeStatus = status && STATUS_KEY.test(status) ? status : undefined;
  const homeCoordinator =
    coordinator && OBJECT_ID.test(coordinator) ? coordinator : undefined;
  const homeCell = cell && OBJECT_ID.test(cell) ? cell : undefined;
  if (!homeFilter && !homeStatus) return null;

  let text = homeFilter ? HOME_FILTER_LABELS[homeFilter] : "";
  if (homeStatus) text = `Status: ${label || homeStatus}`;
  else if (homeCoordinator) text = `Coordenador: ${label}`;
  else if (homeCell) text = `Célula: ${label}`;

  return {
    scope,
    ...(homeFilter ? { homeFilter } : {}),
    ...(homeStatus ? { homeStatus } : {}),
    ...(homeCoordinator ? { homeCoordinator } : {}),
    ...(homeCell ? { homeCell } : {}),
    label: text,
  };
}

const WEEKDAYS_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const WEEKDAYS_LONG = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

/** Brasil não observa horário de verão: o dia civil é sempre UTC-3. */
const SAO_PAULO_OFFSET_MS = -3 * 60 * 60 * 1000;

function parseDayKey(key: string): Date {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function toDayKey(date: Date): string {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

export function shiftDayKey(key: string, days: number): string {
  const date = parseDayKey(key);
  date.setUTCDate(date.getUTCDate() + days);
  return toDayKey(date);
}

/** Dia de São Paulo (YYYY-MM-DD) de um instante ISO. */
export function saoPauloDayKey(iso: string): string {
  return toDayKey(new Date(new Date(iso).getTime() + SAO_PAULO_OFFSET_MS));
}

/** "14:32" no fuso de São Paulo. */
export function saoPauloTime(iso: string): string {
  const shifted = new Date(new Date(iso).getTime() + SAO_PAULO_OFFSET_MS);
  return `${String(shifted.getUTCHours()).padStart(2, "0")}:${String(
    shifted.getUTCMinutes()
  ).padStart(2, "0")}`;
}

/** Horário da movimentação: "14:32" hoje, "Ontem 18:05" no dia anterior. */
export function movementTimeLabel(iso: string, todayKey: string): string {
  const dayKey = saoPauloDayKey(iso);
  if (dayKey === todayKey) return saoPauloTime(iso);
  if (dayKey === shiftDayKey(todayKey, -1)) return `Ontem ${saoPauloTime(iso)}`;
  return `${formatShortDay(dayKey)} ${saoPauloTime(iso)}`;
}

/** Dias corridos desde a entrada no status (dia de São Paulo). */
export function daysSince(iso: string, todayKey: string): number {
  return Math.round(
    (parseDayKey(todayKey).getTime() -
      parseDayKey(saoPauloDayKey(iso)).getTime()) /
      86400000
  );
}

/** "01/10" */
export function formatShortDay(key: string): string {
  const [, month, day] = key.split("-");
  return `${day}/${month}`;
}

/** "Quinta-feira, 01/10/2026" */
export function formatLongDay(key: string): string {
  const [year, month, day] = key.split("-");
  return `${
    WEEKDAYS_LONG[parseDayKey(key).getUTCDay()]
  }, ${day}/${month}/${year}`;
}

/** "Hoje", "Amanhã" ou o dia da semana abreviado. */
export function agendaDayLabel(dayKey: string, todayKey: string): string {
  if (dayKey === todayKey) return "Hoje";
  if (dayKey === shiftDayKey(todayKey, 1)) return "Amanhã";
  return WEEKDAYS_SHORT[parseDayKey(dayKey).getUTCDay()];
}

export function greeting(hour: number): string {
  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";
  return "Boa noite";
}
